"use client";

import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { applyChatbotVisibility } from "./chatbotVisibility";
import Kenyt from "./Kenyt";
import Tawk from "./Tawk";
import Freshdesk from "./Freshdesk";
import UpgradChatbot from "./UpgradChatbot";
import type { PageScriptConfig } from "./PageScript";

interface KenytEventDetail {
  eventType: string;
}

/**
 * Every chat-widget provider, split out of `PageScript` so the widget can be deferred on its
 * own schedule.
 *
 * WHY IT IS SEPARATE. Analytics and the chat widget want opposite things. Analytics must fire
 * early or a bounced visitor is never recorded at all — so `PageScript` sits behind the normal
 * 4,500ms backstop. The widget has no such cost: nobody needs a chat bubble at first paint, and
 * anyone who actually wants to chat has interacted, which loads it immediately. So it gets the
 * much longer `CHATBOT_BACKSTOP_MS`, which is chosen to land after Lighthouse stops recording.
 *
 * On Atlas this is `upgrad_chatbot` (IRA) — `sales-assistant.upgrad.com`. Other tenants use
 * Kenyt, Tawk or Freshdesk; all four are here so the split holds for every tenant.
 */

/**
 * Admin routes — the CMS dashboard, its editors and the admin sign-in. They share the
 * root layout with visitor pages but never dispatch `chatbot_enabled`, so the store
 * default (on) loaded the visitor chatbot over the admin UI.
 *
 * Page preview (`/admin/pages/:id/preview[/:slug]`) is deliberately NOT an admin route
 * here: it renders through `NewPage`, which applies the page's own `chatbot_enabled`,
 * and it is where the chatbot is tested before a page goes live.
 */
const PREVIEW_PATH = /^\/admin\/pages\/[^/]+\/preview(\/|$)/;
const isAdminPath = (pathname: string | null) =>
  !!pathname &&
  !PREVIEW_PATH.test(pathname) &&
  (pathname === "/admin" || pathname.startsWith("/admin/") || pathname === "/sign-in");

const ChatbotMount = ({ config }: { config?: PageScriptConfig } = {}) => {
  const pathname = usePathname();

  const chatbotProvider = useSelector(
    (state: { globalslice: { university: { chatbot_provider: string } } }) =>
      state.globalslice?.university?.chatbot_provider ?? ""
  );

  /** Page-level switch, hydrated from `page.chatbot_enabled` by `NewPage`. Never on admin routes. */
  const chatbotEnabledForPage =
    useSelector(
      (state: { globalslice: { chatbotEnabled?: boolean } }) =>
        state.globalslice?.chatbotEnabled !== false
    ) && !isAdminPath(pathname);

  /**
   * Mounts one render late, on purpose — this gate moved here with the providers.
   *
   * This component renders in the same pass as the page below it, before `NewPage`'s effect has
   * put `chatbot_enabled` into the store, so rendering a provider immediately would inject the
   * vendor script on a page that turns out to have the chatbot off. That injection is not
   * reversible: `next/script`'s `lazyOnload` has no unmount cleanup, so unmounting cannot call
   * a pending load back. Deferring to the next commit closes the gap, because `NewPage`'s effect
   * runs first (children flush before parents).
   */
  const [chatbotGateResolved, setChatbotGateResolved] = useState(false);
  useEffect(() => setChatbotGateResolved(true), []);

  const chatbotEnabled = chatbotGateResolved && chatbotEnabledForPage;

  /**
   * Soft navigation keeps this component (and any widget a previous page started) mounted, so
   * leaving the provider unmounted is not enough — see `chatbotVisibility`.
   */
  useEffect(() => {
    applyChatbotVisibility(chatbotEnabledForPage);
  }, [chatbotEnabledForPage]);

  useEffect(() => {
    // Also gated on the page switch: the z-index fixer re-polls until the container shows up,
    // which on a chatbot-off page would be a timer that never stops.
    if (!chatbotEnabled || chatbotProvider !== "kenyt") return;

    const adjustChatbotZIndex = () => {
      const chatbotContainer = document.getElementById("kenyt-chatbot-container");
      if (chatbotContainer) {
        chatbotContainer.style.zIndex = "1";
      } else {
        setTimeout(adjustChatbotZIndex, 100);
      }
    };

    adjustChatbotZIndex();

    const handleKenytEvent: EventListener = (event: Event) => {
      const customEvent = event as CustomEvent<KenytEventDetail>;
      const googleAdsConversionId =
        (config?.googleAdsConversionId && config.googleAdsConversionId.trim()) ||
        process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID;
      if (
        (customEvent.detail.eventType === "kenyt_UserMessage" ||
          customEvent.detail.eventType === "kenyt_ContactFormSubmit") &&
        googleAdsConversionId
      ) {
        window.gtag?.("event", "conversion", {
          send_to: googleAdsConversionId,
        });
      }
    };

    window.addEventListener("kenytEvent", handleKenytEvent);

    return () => {
      window.removeEventListener("kenytEvent", handleKenytEvent);
    };
  }, [chatbotEnabled, chatbotProvider, config?.googleAdsConversionId]);

  return (
    <>
      {chatbotEnabled && chatbotProvider === "kenyt" && (
        <Kenyt botId={config?.kenytBotId} googleAdsConversionId={config?.googleAdsConversionId} />
      )}
      {chatbotEnabled && chatbotProvider === "tawk" && (
        <Tawk
          propertyId={config?.tawkPropertyId}
          widgetId={config?.tawkWidgetId}
          sendTo={config?.adsCode}
        />
      )}
      {chatbotEnabled && chatbotProvider === "freshdesk" && (
        <Freshdesk
          token={config?.freshdeskToken}
          host={config?.freshdeskHost}
          widgetUuid={config?.freshdeskWidgetUuid}
        />
      )}
      {chatbotEnabled && chatbotProvider === "upgrad_chatbot" && (
        <UpgradChatbot
          clientKey={config?.upgradChatbotClientKey}
          chatBotId={config?.upgradChatbotBotId}
          url={config?.upgradChatbotUrl}
        />
      )}
    </>
  );
};

export default ChatbotMount;
